import React from 'react';
import queryString from 'query-string';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Theme from '@dagensmat/carrot/Theme';
import { Typography } from '@dagensmat/carrot/Components';
import i18n from 'i18n';
import Button, { ButtonLink } from '_common/components/button/Button.style';
import { postLogin } from 'api';
import { ChangeRoleDTO, loggedIn } from '_common/reducers/auth';
import { clearBasket } from '_consumer/reducers/basket';
import { track, SUCCESSFUL_LOGIN, LOGIN_PAGE_VIEW } from 'utils/mixpanel';
import FormField from '_common/components/input/FormField';
import usePageView from '_common/hooks/usePageView';
import { useAppDispatch, useAppSelector } from '_common/hooks/reduxHooks';
import { MediaQuery } from 'utils/mediaQueries';
import FaqsLinkText from '_common/components/text-link/FaqsLinkText';
import { Page } from '@components/page';
import { LoginCallout, LoginCalloutProps } from './LoginCallout';

const LoginAndPasswordButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Theme.Spacings.xl}px;

  a,
  button {
    width: 100%;
    text-align: center;
  }

  ${MediaQuery.tabletUp} {
    flex-direction: row;
    justify-content: space-between;
    a,
    button {
      width: fit-content;
    }
  }
`;

const SignupButton = styled(ButtonLink)`
  width: 100%;
  text-align: center;

  ${MediaQuery.tabletUp} {
    width: fit-content;
  }
`;

const statuses = {
  error: {
    code: 'error',
    message: i18n.t('common:ProblemWithLogin')
  }
} as const;

const LoginPage = () => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [status, setStatus] = React.useState<
    (typeof statuses)['error'] | undefined
  >(undefined);
  const { search } = useLocation();

  const { redirectLogin = '', calloutType } = queryString.parse(search) as {
    redirectLogin?: string;
    calloutType: LoginCalloutProps['type'];
  };

  usePageView(LOGIN_PAGE_VIEW);
  const dispatch = useAppDispatch();

  const isAuthenticated = useAppSelector(({ auth: { authenticated } }) => {
    return authenticated;
  });

  const onSubmitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email || !password) return;

    postLogin({ email, password }).then(
      (
        response: Response & { email?: string; roles?: any[]; uid?: string }
      ) => {
        if (response.ok || response.uid) {
          track(SUCCESSFUL_LOGIN);
          dispatch(loggedIn(response as ChangeRoleDTO));
          dispatch(clearBasket());
        } else {
          setStatus(statuses.error);
        }
      }
    );
  };

  if (isAuthenticated) {
    return <Navigate to={`/${redirectLogin}`} />;
  }

  return (
    <Page>
      <LoginCallout type={calloutType} />
      <form onSubmit={onSubmitHandler}>
        <FormField
          name="email"
          labelText={i18n.t('common:Username')}
          type="email"
          placeholder={i18n.t('common:WriteYourEmail')}
          autoComplete="email"
          tabIndex={0}
          value={email}
          onChange={({ target: { value } }) => {
            setEmail(value);
          }}
          margin={{
            mb: 's'
          }}
        />
        <FormField
          labelText={i18n.t('signup:passwordLabelText')}
          name="password"
          type="password"
          autoComplete="current-password"
          placeholder={i18n.t('common:WriteYourPassword')}
          tabIndex={0}
          value={password}
          onChange={({ target: { value } }) => {
            setPassword(value);
          }}
          margin={{
            mt: 's',
            mb: 's'
          }}
        />
        {status && status.code === statuses.error.code && (
          <Typography variant="paragraphSmall" color="error">
            {status.message}
          </Typography>
        )}
        <LoginAndPasswordButtonContainer>
          <Button tabIndex={0} type="submit" variant="primary">
            {i18n.t('common:SignIn')}
          </Button>
          <ButtonLink tabIndex={-1} to="/reset-password" variant="borderless">
            {i18n.t('common:ForgotPassword')}
          </ButtonLink>
        </LoginAndPasswordButtonContainer>
      </form>
      <Typography variant="paragraphBold" as="p" mt="m" mb="xs">
        {i18n.t('common:NewToDagens')}
      </Typography>
      <SignupButton tabIndex={-1} to="/letsgo" variant="secondary">
        {i18n.t('common:CreateUser')}
      </SignupButton>
      <FaqsLinkText />
    </Page>
  );
};

export default LoginPage;
